import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Handshake } from 'lucide-react';
import Container from '../../components/layout/Container';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import Textarea from '../../components/common/Textarea';
import FormField from '../../components/common/FormField';
import { useStoreContext } from '../../contexts/StoreContext';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { storeService } from '../../services/storeService';
import { authService } from '../../services/authService';
import { unmask } from '../../utils/maskHelpers';

// Página whitelabel "Quero ser revendedor" (Doc Seção 9.7).
// Renderiza apenas em stores businessType=reseller. Se o visitante não
// estiver autenticado, fazemos um fluxo simplificado de cadastro + apply
// na sequência. Se já autenticado, apenas chamamos o endpoint apply.
const BecomeResellerPage = () => {
  const navigate = useNavigate();
  const { store } = useStoreContext();
  const { user, isAuthenticated, login } = useAuth();
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    cpf: user?.cpf || '',
    phone: user?.phone || '',
    message: ''
  });

  if (!store) {
    return (
      <Container>
        <p className="py-12 text-center text-app-text-secondary">Loja não identificada.</p>
      </Container>
    );
  }

  if (store.businessType !== 'reseller') {
    return (
      <Container>
        <Card className="p-6 my-12 text-center">
          <Handshake size={32} className="mx-auto text-app-text-secondary mb-3" />
          <h2 className="text-xl font-bold text-app-text">Programa indisponível</h2>
          <p className="text-app-text-secondary mt-1">
            Esta loja não possui programa de revenda no momento.
          </p>
        </Card>
      </Container>
    );
  }

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.email?.trim()) {
      error('Preencha nome e email.');
      return;
    }
    setLoading(true);
    try {
      // 1) Garante usuário autenticado. Se não tem conta, cria com um signup
      //    "leve" (mínimo: nome, email, password). Login na sequência.
      if (!isAuthenticated) {
        if (!form.password || form.password.length < 6) {
          throw new Error('Senha precisa de ao menos 6 caracteres para criar conta.');
        }
        await authService.signup({
          name: form.name,
          email: form.email,
          password: form.password,
          phone: unmask(form.phone) || null,
          cpf: unmask(form.cpf) || null,
          role: 'client'
        });
        await login(form.email, form.password);
      }
      await storeService.applyAsReseller(store.id);
      success('Candidatura enviada! O dono da loja será notificado.');
      setTimeout(() => navigate('/minha-conta'), 800);
    } catch (err) {
      error(err?.message || 'Falha ao enviar candidatura.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <div className="max-w-xl mx-auto py-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
            <Handshake size={26} className="text-primary" />
          </div>
          <h1 className="text-3xl font-extrabold text-app-text">
            Quero ser revendedor da {store.name}
          </h1>
          <p className="text-app-text-secondary mt-1">
            Preencha o formulário abaixo. O proprietário avalia sua candidatura.
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField label="Nome completo" required>
              <Input value={form.name} onChange={setField('name')} required />
            </FormField>
            <FormField label="Email" required>
              <Input type="email" value={form.email} onChange={setField('email')} required />
            </FormField>
            {!isAuthenticated && (
              <FormField label="Crie uma senha" required helpText="Mínimo 6 caracteres">
                <Input
                  type="password"
                  value={form.password}
                  onChange={setField('password')}
                  required
                />
              </FormField>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="CPF">
                <MaskedInput mask="cpf" value={form.cpf} onChange={setField('cpf')} />
              </FormField>
              <FormField label="Telefone">
                <MaskedInput mask="phone" value={form.phone} onChange={setField('phone')} />
              </FormField>
            </div>
            <FormField label="Por que quer revender?">
              <Textarea
                rows={4}
                value={form.message}
                onChange={setField('message')}
                placeholder="Conte um pouco sobre você, sua audiência…"
              />
            </FormField>
            <Button type="submit" loading={loading} fullWidth size="lg">
              Enviar candidatura
            </Button>
          </form>
        </Card>
      </div>
    </Container>
  );
};

export default BecomeResellerPage;
